import type { ConversionResult, ConversionOptions, ConversionContext } from './types';
import { parseXML } from './parser';
import { validateStructure } from './validators';
import { convertToSerializedNodes } from './converter';
import { createIdGenerator } from './id-generator';
import { CURRENT_SCHEMA_VERSION } from '@builder4/shared/migrations/craft/registry';

// Re-export types for consumers
export type {
  ConversionResult,
  ConversionOptions,
  ValidationError,
  SerializedNodes,
  SerializedNode,
  VersionedPageJson,
  VersionedSectionJson,
} from './types';

const CRAFTJS_VERSION = CURRENT_SCHEMA_VERSION;

/**
 * Convert XML DSL to CraftJS JSON
 *
 * This is the main entry point for the XML DSL converter.
 * Accepts either <page> or <section> as the root element.
 *
 * @param xmlString - XML string in the page builder DSL format
 * @param options - Conversion options
 * @returns ConversionResult with success status, versioned JSON, and any errors
 *
 * @example
 * ```typescript
 * // Full page with sections - returns pageJson
 * const pageXml = `
 *   <page bg="#fff" padding="20px">
 *     <section>
 *       <text><p>Hello World</p></text>
 *     </section>
 *   </page>
 * `;
 *
 * const pageResult = xmlToCraftJson(pageXml);
 * if (pageResult.success && pageResult.pageJson) {
 *   // pageJson format: { ROOT: {...}, nodeId1: {...}, version: 13 }
 *   editor.actions.deserialize(pageResult.pageJson);
 * }
 *
 * // Section only - returns sectionJson
 * const sectionXml = `
 *   <section padding="40,40,40,40" bg="#f5f5f5">
 *     <text><p>Section content</p></text>
 *   </section>
 * `;
 *
 * const sectionResult = xmlToCraftJson(sectionXml);
 * if (sectionResult.success && sectionResult.sectionJson) {
 *   // sectionJson format: { rootNodeId: 'abc', nodes: {...}, version: 13 }
 *   saveSectionTemplate(sectionResult.sectionJson);
 * }
 * ```
 */
export const xmlToCraftJson = (
  xmlString: string,
  options: ConversionOptions = {},
): ConversionResult => {
  const { validateStructure: shouldValidate = true, strictMode = false } = options;

  // Parse XML string
  const parseResult = parseXML(xmlString);
  if (parseResult.errors.length > 0) {
    const hasErrors = parseResult.errors.some((e) => e.severity === 'error');
    if (hasErrors) {
      return {
        success: false,
        errors: parseResult.errors,
      };
    }
  }

  if (!parseResult.root) {
    return {
      success: false,
      errors: [
        {
          severity: 'error',
          type: 'parse',
          message: 'Failed to parse XML - no root element',
          element: 'document',
        },
      ],
    };
  }
  const validationErrors = shouldValidate ? validateStructure(parseResult.root) : [];
  const allErrors = [...parseResult.errors, ...validationErrors];

  // Check for fatal errors
  const hasFatalErrors = allErrors.some((e) => e.severity === 'error');
  if (hasFatalErrors) {
    return {
      success: false,
      errors: allErrors,
    };
  }

  // Create conversion context
  const context: ConversionContext = {
    idGenerator: createIdGenerator(),
    parentId: null,
    errors: [...allErrors],
  };

  // Convert to CraftJS JSON
  const conversionOutput = convertToSerializedNodes(parseResult.root, context);

  // In strict mode, treat warnings as errors
  if (strictMode && context.errors.some((e) => e.severity === 'warning')) {
    if (conversionOutput.type === 'page') {
      return {
        success: false,
        // @ts-expect-error - nodes has ROOT but isn't being inferred
        pageJson: { ...conversionOutput.nodes, version: CRAFTJS_VERSION },
        errors: context.errors,
      };
    }
    return {
      success: false,
      sectionJson: {
        rootNodeId: conversionOutput.rootNodeId,
        nodes: conversionOutput.nodes,
        version: CRAFTJS_VERSION,
      },
      errors: context.errors,
    };
  }

  // Return appropriate format based on root element type
  if (conversionOutput.type === 'page') {
    return {
      success: true,
      // @ts-expect-error - nodes has ROOT but isn't being inferred
      pageJson: { ...conversionOutput.nodes, version: CRAFTJS_VERSION },
      errors: context.errors,
    };
  }

  return {
    success: true,
    sectionJson: {
      rootNodeId: conversionOutput.rootNodeId,
      nodes: conversionOutput.nodes,
      version: CRAFTJS_VERSION,
    },
    errors: context.errors,
  };
};
